import React, { useCallback, useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import moment from "moment"
import {
  FaUser, FaEnvelope, FaIdBadge, FaCalendarAlt, FaBuilding, FaClipboardList,
  FaGraduationCap, FaCheckCircle, FaTimesCircle, FaKey, FaUserShield, FaBolt, FaPlug,
  FaEnvelopeOpenText, FaExclamationTriangle, FaUserCheck
} from "react-icons/fa"
import {
  getUserDetail, sendAdminResetPassword, sendResetPassword, sendResendConfirmation, sendConfirmUserEmail
} from "../../requesters/Backend/UserRequester"
import UpdateEnrolment from "./UserDetail/UpdateEnrolment"
import UnenrolModal from "./UserDetail/UnenrolModal"
import LearnWorldsCourses from "./UserDetail/LearnWorldsCourses"
import UserReports from "./UserDetail/UserReports"
import ConfirmActionModal from "./UserDetail/ConfirmActionModal"
import QuickAction from "./UserDetail/QuickAction"
import runUserAction from "./UserDetail/runUserAction"

const formatDate = value => (value ? moment(value).format("DD MMM YYYY HH:mm") : "-")

function BoolBadge({ value }) {
  if (value === undefined || value === null) return <span className="summary-value">-</span>
  return value
    ? (
      <span className="summary-value text-success">
        <FaCheckCircle className="mr-1" />
        Yes
      </span>
)
    : (
      <span className="summary-value text-muted">
        <FaTimesCircle className="mr-1" />
        No
      </span>
)
}

function UserDetailPage({ baseUrl, currentUser }) {
  const { userId } = useParams()
  const [userDetail, setUserDetail] = useState({})
  const [showModal, setShowModal] = useState(false)
  const [sendingToUser, setSendingToUser] = useState(true)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [showManualConfirmModal, setShowManualConfirmModal] = useState(false)

  const fetchUserDetail = useCallback(() => {
    getUserDetail(baseUrl, userId, currentUser)
      .then(res => {
        if (res?.response) {
          setUserDetail(res.response)
        }
        return null
      })
      .catch(err => {
        toast.error(`${err}, User not found`)
      })
  }, [baseUrl, currentUser, userId])

  useEffect(() => {
    fetchUserDetail()
  }, [fetchUserDetail])

  const handleToggleModal = () => setShowModal(!showModal)
  const handleToggleConfirmationModal = () => setShowConfirmationModal(!showConfirmationModal)
  const handleToggleManualConfirmModal = () => setShowManualConfirmModal(!showManualConfirmModal)

  const handleAdminResetPassword = () => runUserAction(sendAdminResetPassword(baseUrl, userDetail.id), {
    successMessage: "Reset password email sent",
    fallbackError: "Could not send the reset password email",
    onSuccess: () => setShowModal(false)
  })

  const handleSendResetPassword = () => runUserAction(sendResetPassword(baseUrl, userDetail.email), {
    successMessage: "Reset password email sent",
    fallbackError: "Could not send the reset password email",
    onSuccess: () => setShowModal(false)
  })

  const handleResendConfirmation = () => runUserAction(sendResendConfirmation(baseUrl, userDetail.id), {
    successMessage: "Confirmation email sent",
    fallbackError: "Could not send the confirmation email",
    onSuccess: () => setShowConfirmationModal(false)
  })

  const handleManualConfirm = () => runUserAction(sendConfirmUserEmail(baseUrl, userDetail.id), {
    successMessage: "Email address confirmed",
    fallbackError: "Could not confirm this email address",
    onSuccess: () => {
      setShowManualConfirmModal(false)
      fetchUserDetail()
    }
  })

  const handleAdminResetModal = () => {
    setSendingToUser(false)
    setShowModal(true)
  }

  const handleUserResetModal = () => {
    setSendingToUser(true)
    setShowModal(true)
  }

  const { unit, active_program: activeProgram, active_enrolment: activeEnrolment } = userDetail
  const enrolmentStatus = activeEnrolment?.status
  const isEnrolled = (enrolmentStatus || "").toLowerCase() === "active"
  const isConfirmed = Boolean(userDetail.confirmed_at)

  return (
    <div className="content">
      <div className="container-fluid">
        <ConfirmActionModal
          onConfirm={sendingToUser ? handleSendResetPassword : handleAdminResetPassword}
          onHide={handleToggleModal}
          show={showModal}
          title={sendingToUser ? "Reset Password (User)" : "Reset Password (Admin)"}>
          <p>
            {sendingToUser ? `This will send a reset password email to ${userDetail.email}`
            : `This will send a reset password email to your own email address: ${currentUser.email}. Please copy paste the link in the email into an incognito tab`}
          </p>
        </ConfirmActionModal>

        <ConfirmActionModal
          onConfirm={handleResendConfirmation}
          onHide={handleToggleConfirmationModal}
          show={showConfirmationModal}
          title="Resend Confirmation Email">
          <p>{`This will send an email confirmation link to ${userDetail.email}. If their previous link has expired, a new one is issued.`}</p>
        </ConfirmActionModal>

        <ConfirmActionModal
          confirmLabel="Confirm Email"
          confirmVariant="danger"
          onConfirm={handleManualConfirm}
          onHide={handleToggleManualConfirmModal}
          show={showManualConfirmModal}
          title="Manually Confirm Email">
          <p>{`This marks ${userDetail.email} as verified without the user clicking a confirmation link, and lets them log in immediately.`}</p>
          <p className="text-danger mb-0">
            Only do this once you have verified the user owns this address by another channel.
            Use it when our confirmation emails cannot reach them at all — for example a
            corporate mail filter is blocking them. This action is audit logged against your account.
          </p>
        </ConfirmActionModal>

        <div className="form-container program-edit">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/backend/users">Users</Link>
              </li>
              <li aria-current="page" className="breadcrumb-item active">{userDetail.name}</li>
            </ol>
          </nav>

          <div className="program-edit-header">
            <div>
              <h1>{userDetail.name}</h1>
              <div className="program-edit-subtitle">{userDetail.email}</div>
              <div className="program-edit-meta">
                <span className={`status-badge ${isEnrolled ? "" : "inactive"}`}>
                  <span className="status-dot" />
                  {`Enrolment: ${enrolmentStatus || "None"}`}
                </span>
                <span className={`status-badge ${isConfirmed ? "" : "inactive"}`}>
                  <span className="status-dot" />
                  {isConfirmed ? "Email confirmed" : "Email unconfirmed"}
                </span>
                {userDetail.created_at ? (
                  <span>
                    Member since:
                    {" "}
                    {formatDate(userDetail.created_at)}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="program-edit-actions">
              <button className="btn btn-outline-main btn-icon" onClick={handleUserResetModal} type="button">
                <FaKey />
                Reset Password
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-8">
              <div className="card-section">
                <div className="card summary-card">
                  <div className="card-header">
                    <h5>
                      <FaUser />
                      Account Details
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaUser />
                        Name
                      </span>
                      <span className="summary-value">{userDetail.name || "-"}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaEnvelope />
                        Email
                      </span>
                      <span className="summary-value">{userDetail.email || "-"}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaIdBadge />
                        User ID
                      </span>
                      <span className="summary-value">{userDetail.id || "-"}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaCalendarAlt />
                        Created At
                      </span>
                      <span className="summary-value">{formatDate(userDetail.created_at)}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaEnvelopeOpenText />
                        Email Confirmed
                      </span>
                      <span className="summary-value">
                        {isConfirmed ? (
                          <span className="text-success">
                            <FaCheckCircle className="mr-1" />
                            {formatDate(userDetail.confirmed_at)}
                          </span>
                        ) : (
                          <span className="text-danger">
                            <FaExclamationTriangle className="mr-1" />
                            Not confirmed
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaBuilding />
                        Unit
                      </span>
                      <span className="summary-value">
                        {unit ? (
                          <Link to={`/backend/units/${unit.id}`}>{unit.name}</Link>
                        ) : "-"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card summary-card">
                  <div className="card-header">
                    <h5>
                      <FaClipboardList />
                      Enrolment
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaGraduationCap />
                        Current Program
                      </span>
                      <span className="summary-value">
                        {activeProgram ? (
                          <Link to={`/backend/programs/${activeProgram.id}`}>
                            {activeProgram.name}
                            {" - "}
                            {activeProgram.unique_id}
                          </Link>
                        ) : "-"}
                      </span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaCalendarAlt />
                        Enrolled At
                      </span>
                      <span className="summary-value">{formatDate(activeEnrolment?.created_at)}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaClipboardList />
                        Enrolment Status
                      </span>
                      <span className="summary-value">{enrolmentStatus || "-"}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaPlug />
                        Kajabi Signed Up
                      </span>
                      <BoolBadge value={activeEnrolment?.kajabi_signed_up} />
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaPlug />
                        LearnWorlds Signed Up
                      </span>
                      <BoolBadge value={activeEnrolment?.learn_world_signed_up} />
                    </div>
                  </div>
                </div>

                <LearnWorldsCourses
                  baseUrl={baseUrl}
                  currentUser={currentUser}
                  learnWorldUserId={userDetail.learn_world_user_id} />
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card summary-card">
                <div className="card-header">
                  <h5>
                    <FaUserShield />
                    Summary
                  </h5>
                </div>
                <div className="card-body">
                  <div className="summary-row">
                    <span className="summary-label">Enrolment Status</span>
                    <span className="summary-value">
                      <span className={`status-badge ${isEnrolled ? "" : "inactive"}`}>
                        <span className="status-dot" />
                        {enrolmentStatus || "None"}
                      </span>
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">
                      <FaBuilding />
                      Unit
                    </span>
                    <span className="summary-value">
                      {unit ? (
                        <Link to={`/backend/units/${unit.id}`}>{unit.name}</Link>
                      ) : "—"}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">
                      <FaGraduationCap />
                      Program
                    </span>
                    <span className="summary-value">
                      {activeProgram ? (
                        <Link to={`/backend/programs/${activeProgram.id}`}>{activeProgram.name}</Link>
                      ) : "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h5>
                    <FaBolt />
                    Quick Actions
                  </h5>
                </div>
                <div className="card-body quick-actions">
                  <QuickAction icon={FaKey} label="Reset Password (Send to User)" onClick={handleUserResetModal} />
                  <QuickAction icon={FaUserShield} label="Reset Password (Send to Admin)" onClick={handleAdminResetModal} />
                  {!isConfirmed && (
                    <>
                      <QuickAction
                        icon={FaEnvelopeOpenText}
                        label="Resend Confirmation Email"
                        onClick={handleToggleConfirmationModal} />
                      <QuickAction
                        icon={FaUserCheck}
                        label="Manually Confirm Email"
                        onClick={handleToggleManualConfirmModal} />
                    </>
                  )}
                  <UpdateEnrolment baseUrl={baseUrl} refetch={fetchUserDetail} userDetail={userDetail} />
                  {activeProgram && (
                    <UnenrolModal baseUrl={baseUrl} refetch={fetchUserDetail} userDetail={userDetail} />
                  )}
                </div>
              </div>

              {userDetail.id && (
                <UserReports
                  baseUrl={baseUrl}
                  userId={userDetail.id} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserDetailPage
